import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { 
  Baby, Wifi, Car, Droplets, Coffee, Tv, TreePine, Zap, Wind, 
  Bath, Refrigerator, Flame, Cross, Camera, ChevronLeft, ChevronRight 
} from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const iconMap = {
  Baby, Wifi, Car, Droplets, Coffee, Tv, TreePine, Zap, Wind, 
  Bath, Refrigerator, Flame, Cross, Camera
};

export default function Home() {
  const [settings, setSettings] = useState(null);
  const [amenities, setAmenities] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);

  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true }, [
    Autoplay({ delay: 5000, stopOnInteraction: true, stopOnMouseEnter: true })
  ]);

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setCurrentSlide(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    return () => emblaApi.off("select", onSelect);
  }, [emblaApi, onSelect]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [settingsRes, amenitiesRes, faqsRes] = await Promise.all([
          axios.get(`${API}/settings`),
          axios.get(`${API}/amenities`),
          axios.get(`${API}/faqs`)
        ]);
        setSettings(settingsRes.data);
        setAmenities(amenitiesRes.data);
        setFaqs(faqsRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7]">
        <div className="spinner" />
      </div>
    );
  }

  const heroSlides = settings?.hero_slides || [
    { url: "/assets/home_banner1.png", caption: "Book your Room with best prices" },
    { url: "/assets/home_banner2.png", caption: "We make your life Delicious & Tasteful" },
    { url: "/assets/home_banner3.png", caption: "Stay & Celebrate Your Wonderful Moments" }
  ];

  const propertyActivities = settings?.property_activities || [
    { name: "Swimming Pool" }, { name: "Badminton Court" }, { name: "Playing Cards" },
    { name: "Carrom Board" }, { name: "Kids Play Systems" }, { name: "Basket Ball Court" },
    { name: "Cricket" }, { name: "Chess" }, { name: "Beach Access" }
  ];

  const outdoorActivities = settings?.outdoor_activities || [
    {
      name: "Boat House",
      description: "Boat House – Mudaliarkuppam – The Raindrop Boat House, Mudaliarkuppam, is located on the East Coast Road. This backwater boating site offers a unique site for water sports and picturesque beach islands that serve as an ideal picnic spot. It is situated just about 5 minutes drive from the stay around 6 km.",
      image: "/assets/boat_house.png"
    },
    {
      name: "Panaiyur Beach",
      description: "It is situated just about 3 minutes drive from the stay around 1 km.",
      image: "/assets/panaiyur_beach.png"
    },
    {
      name: "Alampara Fort",
      description: "This fort is said to have built by the Muslim kings at the end of the 17th Century A.D, and was under the control of Nawab Dost Alikhan in 1735 A.D. This fort was gifted to a French Governor Dupleix by Thakkana Subedar Musaffar –Jung in 1750 A.D. for the help rendered by him. This fort was captured and demolished by Sir Thomas Eyre Coote the then British Commander in 1760 A.D. Today only the ruins are found. It is situated just about 10 minutes drive from the stay around 8 km.",
      image: "/assets/alampara_fort.png"
    },
    {
      name: "Kanthaswamy Temple",
      description: "Constructed in the Dravidian style of architecture, the temple was constructed during the Cholas during the 10th century. Murugan is worshipped as Kanthaswamy along with his consorts Valli and Deivasena. The temple is believed to have been visited by Arunagirinathar, a 15th-century Murugan devotee and composed Thiruppugazh at this place. It is situated just about 10 minutes drive from the stay around 6 km.",
      image: "/assets/kanthaswamy_temple.png"
    },
    {
      name: "Pondicherry",
      description: "The closest city from the stay is about 40 km reachable within 35 minutes drive and can cater to many entertainment and sight seeing activities.",
      image: "/assets/pondicherry.png"
    }
  ];

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="home-page">
      <Header />
      
      {/* Hero Carousel - Enhanced with Modern Design */}
      <section className="relative h-screen overflow-hidden" data-testid="hero-carousel">
        <div className="h-full" ref={emblaRef}>
          <div className="flex h-full">
            {heroSlides.map((slide, index) => (
              <div key={index} className="flex-[0_0_100%] min-w-0 relative h-full">
                <div
                  className="absolute inset-0 bg-cover bg-center transition-transform duration-1000"
                  style={{ backgroundImage: `url(${slide.url})` }}
                />
                {/* Artistic gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-black/60 via-transparent to-black/50" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                
                {/* Animated geometric shapes */}
                <div className="absolute inset-0 overflow-hidden opacity-10">
                  <div className="absolute top-1/4 -left-20 w-96 h-96 bg-[#E67E22] rounded-full filter blur-3xl animate-float"></div>
                  <div className="absolute bottom-1/4 -right-20 w-96 h-96 bg-[#F39C12] rounded-full filter blur-3xl animate-float delay-300"></div>
                </div>

                <div className="relative z-10 h-full flex items-center justify-center px-4">
                  <div className="max-w-4xl w-full">
                    <div className="flex justify-center items-center">
                      {/* Centered Text content */}
                      <div className="text-white space-y-8 animate-slide-in-left text-center">
                        {/* Top badge */}
                        <div className="inline-flex items-center space-x-2 px-5 py-2.5 bg-white/10 backdrop-blur-lg rounded-full border border-white/20">
                          <div className="w-2 h-2 bg-[#F39C12] rounded-full animate-pulse"></div>
                          <span className="text-sm font-bold tracking-widest">LUXURY STAYS</span>
                        </div>
                        
                        {/* Main heading */}
                        <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-bold leading-tight" style={{textShadow: '3px 5px 10px rgba(0,0,0,0.7)'}}>
                          {slide.caption}
                        </h1>
                        
                        {/* Decorative line */}
                        <div className="flex items-center justify-center space-x-4">
                          <div className="h-1 w-24 bg-gradient-to-r from-[#E67E22] to-[#F39C12] rounded-full"></div>
                          <div className="h-1 w-12 bg-white/30 rounded-full"></div>
                        </div>
                        
                        {/* Subtitle */}
                        {/* <p className="text-xl md:text-2xl text-gray-200 font-light leading-relaxed">
                          Experience tranquility in nature's embrace
                        </p> */}
                        
                        {/* CTA Buttons */}
                        <div className="flex flex-col sm:flex-row gap-4 pt-4 justify-center">
                          <Link
                            to="/bookings"
                            className="group relative overflow-hidden bg-gradient-to-r from-[#E67E22] to-[#F39C12] text-white font-bold py-4 px-10 rounded-full transition-all duration-300 hover:shadow-2xl hover:scale-105"
                            data-testid={`hero-cta-${index}`}
                          >
                            <span className="relative z-10 flex items-center justify-center">
                              Check Availability
                              <svg className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                              </svg>
                            </span>
                            <div className="absolute inset-0 bg-gradient-to-r from-[#D35400] to-[#E67E22] opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                          </Link>
                          <Link
                            to="/gallery"
                            className="group bg-white/10 backdrop-blur-md border-2 border-white/30 hover:bg-white hover:border-white text-white hover:text-[#2C3E50] font-bold py-4 px-10 rounded-full transition-all duration-300"
                          >
                            <span className="flex items-center justify-center">
                              View Gallery
                              <svg className="ml-2 w-5 h-5 group-hover:rotate-90 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                            </span>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Carousel Controls - Enhanced */}
        <button
          onClick={scrollPrev}
          className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-20 w-14 h-14 bg-white/10 backdrop-blur-md hover:bg-gradient-to-r hover:from-[#E67E22] hover:to-[#F39C12] rounded-full flex items-center justify-center text-white transition-all duration-300 border border-white/20 hover:scale-110 hover:shadow-glow"
          data-testid="carousel-prev"
          aria-label="Previous slide"
        >
          <ChevronLeft size={28} />
        </button>
        <button
          onClick={scrollNext}
          className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-20 w-14 h-14 bg-white/10 backdrop-blur-md hover:bg-gradient-to-r hover:from-[#E67E22] hover:to-[#F39C12] rounded-full flex items-center justify-center text-white transition-all duration-300 border border-white/20 hover:scale-110 hover:shadow-glow"
          data-testid="carousel-next"
          aria-label="Next slide"
        >
          <ChevronRight size={28} />
        </button>
      </section>

      {/* About Section - Enhanced */}
      <section className="py-24 px-4 bg-gradient-to-b from-white to-[#FDFBF7]" data-testid="about-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="space-y-6 animate-slide-in-left">
              <div className="inline-flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-[#FEF3E2] to-[#FFE5CC] rounded-full">
                <div className="w-2 h-2 bg-[#E67E22] rounded-full animate-pulse"></div>
                <span className="text-[#E67E22] text-sm font-bold tracking-widest">DISCOVER WYLDWOODS</span>
              </div>
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-[#2C3E50] leading-tight">
                WHERE SERENITY<br />
                <span className="gradient-text-sunset">MEETS CONVENIENCE</span>
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-[#E67E22] to-[#F39C12] rounded-full"></div>
              <p className="text-[#7F8C8D] text-lg leading-relaxed">
                {settings?.about_text || "Escape the chaos of urban life to WyldWoods, a tranquil village stay nestled amidst nature's splendor, yet conveniently located near the city making it an ideal getaway for those seeking relaxation and connection with the natural world. WyldWoods offers you Cozy, rustic-chic accommodations with modern amenities, Easy access to nearby cities, attractions, fun filled activities and Warm hospitality, tailored to your needs."}
              </p>
              <Link to="/bookings" className="inline-block btn-primary mt-4">
                Explore Rooms
              </Link>
            </div>
            <div className="relative animate-slide-in-right">
              <div className="absolute -inset-4 bg-gradient-to-r from-[#E67E22] to-[#F39C12] rounded-3xl opacity-20 blur-2xl"></div>
              <div className="relative image-hover rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src={settings?.about_image || "/assets/home1.png"}
                  alt="WyldWoods"
                  className="w-full h-[500px] object-cover"
                />
              </div>
              {/* <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-gradient-to-br from-[#E67E22] to-[#F39C12] rounded-3xl flex items-center justify-center text-white shadow-xl animate-float">
                <div className="text-center">
                  <div className="text-4xl font-bold">5+</div>
                  <div className="text-sm">Years</div>
                </div>
              </div> */}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Preview Section - Enhanced */}
      <section className="py-24 px-4 bg-gradient-to-br from-white via-[#FDFBF7] to-[#FFF8F0] relative overflow-hidden" data-testid="faq-preview-section">
        {/* Decorative background elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-[#E67E22]/10 to-transparent rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-[#F39C12]/10 to-transparent rounded-full blur-3xl"></div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          {/* Section Header */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-[#FEF3E2] to-[#FFE5CC] rounded-full mb-4">
              <div className="w-2 h-2 bg-[#E67E22] rounded-full animate-pulse"></div>
              <span className="text-[#E67E22] text-sm font-bold tracking-widest">YOUR QUESTIONS ANSWERED</span>
            </div>
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-[#2C3E50] mb-4">
              Frequently Asked <span className="gradient-text">Questions</span>
            </h2>
            <p className="text-[#7F8C8D] text-lg max-w-2xl mx-auto">Everything you need to know about your stay at WyldWoods</p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Left side - Enhanced image gallery */}
            <div className="space-y-6 animate-slide-in-left">
              {/* Main image */}
              <div className="relative group">
                <div className="absolute -inset-4 bg-gradient-to-br from-[#E67E22] to-[#F39C12] rounded-3xl opacity-20 blur-2xl group-hover:opacity-30 transition-opacity duration-500"></div>
                <div className="relative image-hover rounded-3xl overflow-hidden shadow-2xl">
                  <img
                    src="/assets/galary20.jpg"
                    alt="WyldWoods Property Exterior"
                    className="w-full h-[350px] object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
                  <div className="absolute bottom-6 left-6 text-white">
                    <div className="flex items-center space-x-2 mb-2">
                      <div className="w-2 h-2 bg-[#F39C12] rounded-full animate-pulse"></div>
                      <span className="text-sm font-semibold">Outdoor View</span>
                    </div>
                    <h3 className="font-serif text-2xl font-bold">Serene Surroundings</h3>
                  </div>
                </div>
              </div>
              
              {/* Secondary image */}
              <div className="relative group">
                <div className="absolute -inset-4 bg-gradient-to-br from-[#F39C12] to-[#E67E22] rounded-3xl opacity-20 blur-2xl group-hover:opacity-30 transition-opacity duration-500"></div>
                <div className="relative image-hover rounded-3xl overflow-hidden shadow-2xl">
                  <img
                    src="/assets/indoor.png"
                    alt="WyldWoods Room Interior"
                    className="w-full h-[280px] object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
                  <div className="absolute bottom-6 left-6 text-white">
                    <div className="flex items-center space-x-2 mb-2">
                      <div className="w-2 h-2 bg-[#E67E22] rounded-full animate-pulse"></div>
                      <span className="text-sm font-semibold">Interior Design</span>
                    </div>
                    <h3 className="font-serif text-2xl font-bold">Comfortable Rooms</h3>
                  </div>
                </div>
              </div>

              {/* Feature stats */}
              <div className="grid grid-cols-3 gap-4">
                <div className="premium-card p-5 text-center card-hover">
                  <div className="text-3xl font-bold gradient-text mb-2">50+</div>
                  <div className="text-sm text-[#7F8C8D]">Happy Guests</div>
                </div>
                <div className="premium-card p-5 text-center card-hover">
                  <div className="text-3xl font-bold gradient-text mb-2">4.8</div>
                  <div className="text-sm text-[#7F8C8D]">Rating</div>
                </div>
                <div className="premium-card p-5 text-center card-hover">
                  <div className="text-3xl font-bold gradient-text mb-2">24/7</div>
                  <div className="text-sm text-[#7F8C8D]">Support</div>
                </div>
              </div>
            </div>

            {/* Right side - FAQ accordion */}
            <div className="animate-slide-in-right">
              <div className="premium-card p-8 shadow-2xl">
                <Accordion type="single" collapsible className="w-full space-y-4">
                  {faqs.slice(0, 8).map((faq, index) => (
                    <AccordionItem 
                      key={faq.id || index} 
                      value={`faq-${index}`}
                      className="border-none bg-gradient-to-br from-[#FDFBF7] to-white rounded-xl px-6 py-2 transition-all duration-300 hover:shadow-lg hover:scale-[1.02]"
                    >
                      <AccordionTrigger className="text-left text-[#2C3E50] font-semibold hover:text-[#E67E22] py-5 [&[data-state=open]]:text-[#E67E22] group no-underline hover:no-underline [&>svg]:text-[#E67E22]">
                        <div className="flex items-center space-x-4 w-full">
                          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#E67E22] to-[#F39C12] flex items-center justify-center flex-shrink-0 text-white font-bold text-sm group-hover:scale-110 group-hover:rotate-6 transition-all duration-300 shadow-md">
                            Q{index + 1}
                          </div>
                          <span className="flex-1 font-serif text-lg md:text-xl font-bold tracking-tight">{faq.question}</span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="text-[#7F8C8D] leading-relaxed pl-14 pr-4 pb-5">
                        <div className="flex items-start space-x-4">
                          <div className="w-1 h-full bg-gradient-to-b from-[#E67E22] to-[#F39C12] rounded-full"></div>
                          <p className="text-base md:text-lg font-sans">{faq.answer}</p>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
                
                {/* View all FAQs CTA */}
                <div className="mt-8 text-center">
                  <Link
                    to="/facilities-and-faqs"
                    className="group inline-flex items-center space-x-2 bg-gradient-to-r from-[#E67E22] to-[#F39C12] text-white font-bold py-4 px-8 rounded-xl transition-all duration-300 hover:shadow-xl hover:scale-105"
                  >
                    <span>View All FAQs</span>
                    <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Amenities Section - Enhanced */}
      <section className="py-24 px-4 bg-white relative overflow-hidden" data-testid="amenities-section">
        <div className="absolute top-0 left-0 w-full h-full opacity-5">
          <div className="absolute top-20 right-20 w-72 h-72 bg-[#E67E22] rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-20 left-20 w-72 h-72 bg-[#F39C12] rounded-full filter blur-3xl"></div>
        </div>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-[#FEF3E2] to-[#FFE5CC] rounded-full mb-4">
              <div className="w-2 h-2 bg-[#E67E22] rounded-full animate-pulse"></div>
              <span className="text-[#E67E22] text-sm font-bold tracking-widest">WHAT WE OFFER</span>
            </div>
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-[#2C3E50] mb-4">
              Amenities 
            </h2>
            <p className="text-[#7F8C8D] text-lg max-w-2xl mx-auto">Everything you need for a perfect stay</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-8">
            {amenities.map((amenity, index) => {
              const IconComponent = iconMap[amenity.icon] || Wifi;
              return (
                <div 
                  key={amenity.id || index} 
                  className="text-center group animate-scale-in"
                  style={{ animationDelay: `${index * 50}ms` }}
                  data-testid={`amenity-${index}`}
                >
                  <div className="amenity-icon mx-auto">
                    <IconComponent size={32} className="group-hover:animate-bounce" />
                  </div>
                  <p className="text-sm text-[#2C3E50] font-semibold tracking-wide group-hover:text-[#E67E22] transition-colors">
                    {amenity.name}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Property Activities Section - Enhanced */}
      <section className="py-24 px-4 bg-gradient-to-b from-[#FDFBF7] to-white" data-testid="activities-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-[#FEF3E2] to-[#FFE5CC] rounded-full mb-4">
              <div className="w-2 h-2 bg-[#E67E22] rounded-full animate-pulse"></div>
              <span className="text-[#E67E22] text-sm font-bold tracking-widest">FUN & RECREATION</span>
            </div>
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-[#2C3E50] mb-4">
              Property <span className="gradient-text">Activities</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="relative animate-slide-in-left">
              <div className="absolute -inset-4 bg-gradient-to-br from-[#E67E22] to-[#F39C12] rounded-3xl opacity-20 blur-2xl"></div>
              <div className="relative image-hover rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="/assets/property_activities.png"
                  alt="Activities"
                  className="w-full h-[500px] object-cover"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 animate-slide-in-right">
              {propertyActivities.map((activity, index) => (
                <div
                  key={index}
                  className="premium-card p-5 flex items-center space-x-3 card-hover"
                  data-testid={`activity-${index}`}
                >
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#E67E22] to-[#F39C12] flex items-center justify-center flex-shrink-0">
                    <ChevronRight size={20} className="text-white" />
                  </div>
                  <span className="text-[#2C3E50] font-semibold">{activity.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Outdoor Activities Section - Enhanced */}
      <section className="py-24 px-4 bg-white" data-testid="outdoor-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-[#FEF3E2] to-[#FFE5CC] rounded-full mb-4">
              <div className="w-2 h-2 bg-[#E67E22] rounded-full animate-pulse"></div>
              <span className="text-[#E67E22] text-sm font-bold tracking-widest">EXPLORE SURROUNDINGS</span>
            </div>
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-[#2C3E50] mb-4">
              Outdoor <span className="gradient-text">Adventures</span>
            </h2>
            <p className="text-[#7F8C8D] text-lg max-w-3xl mx-auto">Discover the beauty and culture of our surroundings</p>
          </div>
          <div className="space-y-20">
            {outdoorActivities.map((activity, index) => (
              <div
                key={index}
                className={`grid md:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? "md:flex-row-reverse" : ""
                }`}
                data-testid={`outdoor-activity-${index}`}
              >
                <div className={`${index % 2 === 1 ? "md:order-2" : ""} animate-fade-in-up`}>
                  <div className="relative image-hover rounded-3xl overflow-hidden shadow-2xl group">
                    <img
                      src={activity.image}
                      alt={activity.name}
                      className="w-full h-[400px] object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </div>
                </div>
                <div className={`${index % 2 === 1 ? "md:order-1" : ""} space-y-6 animate-fade-in-up delay-200`}>
                  <div className="inline-block px-4 py-2 bg-gradient-to-r from-[#E67E22] to-[#F39C12] text-white rounded-full text-sm font-bold">
                    #{index + 1}
                  </div>
                  <h3 className="font-serif text-3xl md:text-4xl font-bold text-[#2C3E50]">
                    {activity.name}
                  </h3>
                  <div className="w-20 h-1 bg-gradient-to-r from-[#E67E22] to-[#F39C12] rounded-full"></div>
                  <p className="text-[#7F8C8D] text-lg leading-relaxed">
                    {activity.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner - Enhanced */}
      <section
        className="relative py-32 px-4 bg-cover bg-center bg-fixed overflow-hidden"
        style={{ backgroundImage: "url(/assets/home_bottom_banner.png)" }}
        data-testid="cta-banner"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-[#2C3E50]/90 via-[#E67E22]/80 to-[#F39C12]/90" />
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}></div>
        <div className="relative z-10 text-center text-white max-w-4xl mx-auto space-y-8">
          <div className="inline-block px-6 py-2 bg-white/10 backdrop-blur-md rounded-full border border-white/20 mb-4 animate-bounce-in">
            <span className="text-sm font-semibold tracking-widest">LIMITED TIME OFFER</span>
          </div>
          <h2 className="font-serif text-4xl md:text-6xl lg:text-7xl font-bold animate-fade-in-up" style={{textShadow: '2px 4px 8px rgba(0,0,0,0.5)'}}>
            RELAX AND ENJOY<br />
            <span className="gradient-text-sunset">YOUR HOLIDAY</span>
          </h2>
          <p className="text-xl md:text-2xl text-gray-200 max-w-2xl mx-auto animate-fade-in-up delay-200">
            Book now and get exclusive discounts on your next stay
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up delay-300">
            <Link
              to="/bookings"
              className="btn-gradient text-lg px-12 py-4"
              data-testid="cta-book-now"
            >
              Book Your Stay Now
            </Link>
            <Link
              to="/contact"
              className="btn-ghost text-lg px-12 py-4"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
